import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/stores/session';
import { legalService, LEGAL_SITE_URL, type LegalDoc } from '@/services/legal';
import { privacyService } from '@/services/privacy';
import { authService } from '@/services/auth';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { toast } from '@/stores/ui';
import { fmtDate } from '@/lib/format';

/** Términos y privacidad con aceptación probada (0079 · estándar Cassimmit
 *  61 a 63). Si hay una versión que la persona no ha aceptado, la app queda
 *  detrás de esta pantalla: casilla sin marcar, enlaces a la versión exacta y,
 *  para quien no acepta, descargar sus datos o pedir el cierre de la cuenta. */
export function LegalGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const user = useSession((s) => s.user);
  const role = useSession((s) => s.role);
  const [state, setState] = useState<'checking' | 'ok' | 'pending' | 'error'>('checking');
  const [docs, setDocs] = useState<LegalDoc[]>([]);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [tryN, setTry] = useState(0);

  useEffect(() => {
    let alive = true;
    setState('checking');
    (async () => {
      if (!user) {
        if (alive) setState('ok');
        return;
      }
      try {
        const pend = await legalService.settle();
        if (!alive) return;
        setDocs(pend);
        setState(pend.length ? 'pending' : 'ok');
      } catch {
        if (alive) setState('error');
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, tryN]);

  if (state === 'ok') return <>{children}</>;
  if (state === 'checking') return null;
  if (state === 'error') {
    return (
      <div className="grid min-h-dvh place-items-center px-4">
        <div className="w-full max-w-md">
          <ErrorState what={t('legal.statusWhat')} onRetry={() => setTry((n) => n + 1)} />
        </div>
      </div>
    );
  }

  const accept = async () => {
    if (!checked) return;
    setBusy(true);
    const ok = await legalService.accept('reaccept_checkbox');
    setBusy(false);
    if (ok) setTry((n) => n + 1);
    else toast(t('errors.generic.what'), 'info');
  };

  const download = async () => {
    setBusy(true);
    try {
      const pkg = await privacyService.exportMyData({ fullName: user?.fullName ?? '', email: user?.email ?? '' });
      const url = URL.createObjectURL(new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vaultex-mis-datos.json';
      a.click();
      URL.revokeObjectURL(url);
      toast(t('privacy.exported'));
    } catch {
      toast(t('errors.generic.what'), 'info');
    } finally {
      setBusy(false);
    }
  };

  const requestClose = async () => {
    setBusy(true);
    const res = await privacyService.requestDeletion();
    setBusy(false);
    toast(res.ok ? t('privacy.deleteRequested', { date: res.dueDate ?? '' }) : t('errors.generic.what'), 'info');
  };

  const isClient = role === 'client';
  const date = (d: string) => fmtDate(`${d}T12:00:00`);

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-8">
      <section aria-labelledby="legal-title" data-testid="legal-gate"
        className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h1 id="legal-title" className="font-display text-lg font-semibold">{t('legal.title')}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t('legal.body')}</p>
        <ul className="mt-4 space-y-2 text-sm">
          {docs.map((d) => (
            <li key={d.kind}>
              <a href={`${LEGAL_SITE_URL}${d.url}`} target="_blank" rel="noopener"
                className="font-medium text-primary underline underline-offset-2">
                {t(`legal.kind.${d.kind}`)}
              </a>{' '}
              <span className="text-muted-foreground">{t('legal.versionLine', { version: d.version, date: date(d.effectiveAt) })}</span>
            </li>
          ))}
        </ul>

        {!declined ? (
          <>
            <label className="mt-5 flex cursor-pointer items-start gap-2.5">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                data-testid="legal-accept-check" className="mt-0.5 h-4 w-4 shrink-0 accent-primary" />
              <span className="text-sm leading-relaxed">{t('legal.checkbox')}</span>
            </label>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button onClick={() => void accept()} loading={busy} disabled={!checked} data-testid="legal-accept">
                {t('legal.accept')}
              </Button>
              <Button variant="ghost" onClick={() => setDeclined(true)}>{t('legal.decline')}</Button>
            </div>
          </>
        ) : (
          <div className="mt-5 space-y-3">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t(isClient ? 'legal.declinedClient' : 'legal.declinedStaff')}
            </p>
            <div className="flex flex-wrap gap-2">
              {isClient && (
                <>
                  <Button variant="glass" onClick={() => void download()} loading={busy}>{t('legal.download')}</Button>
                  <Button variant="glass" onClick={() => void requestClose()} loading={busy}>{t('legal.close')}</Button>
                </>
              )}
              <Button variant="ghost" onClick={() => setDeclined(false)}>{t('legal.back')}</Button>
              <Button variant="ghost" onClick={() => void authService.signOut()}>{t('legal.signOut')}</Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
